import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import { useMutation } from 'hooks/apollo';
import { REMOVE_ACCOUNT_STATEMENT } from './mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useSubscription } from 'hooks/apollo';
import { ACCOUNT_STATEMENT_REMOVED } from './subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import IconButton from 'components/common/buttons/IconButton';
import CheckboxTableCell from 'components/common/table/CheckboxTableCell';
import ActionsTableCell from 'components/common/table/ActionsTableCell';

const AccountStatementItem = ({ canWrite, data }) => {
  const { t } = useTranslation();
  const historyContext = useHistoryWithContext();
  const [disabled, setDisabled] = useState(false);
  const confirm = useConfirmDialog();
  const addToast = useToast();

  const handleEdit = useCallback(() => {
    historyContext.push(`/accountStatement/${data.configId}`);
  }, [historyContext, data]);

  const [deleteMethod] = useMutation(REMOVE_ACCOUNT_STATEMENT, {
    onError: () => {
      setDisabled(false);
    }
  });

  const handleDelete = useCallback(() => {
    confirm(
      () => {
        setDisabled(true);
        deleteMethod({ variables: { input: { configId: data.configId } } });
      },
      {
        title: t('AccountStatement.DeleteAccountStatement'),
        content: t('AccountStatement.AreYouSureDelete')
      }
    );
  }, [confirm, t, data, deleteMethod]);

  useSubscription(ACCOUNT_STATEMENT_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('AccountStatement.AccountStatementRemoved'), 'success');
      client.cache.evict({
        id: `AccountStatementListItem:{"configId":${data.accountStatementDeleted.configId}}`
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'accountStatements' });
      client.cache.gc();
    }
  });

  return (
    <TableRow hover key={data.configId}>
      <TableCell>{data.configName}</TableCell>
      <TableCell>{data.configVersion}</TableCell>
      <CheckboxTableCell disabled={true} checked={data.activeYN} />
      <ActionsTableCell>
        <IconButton
          size='small'
          variant='text'
          color='primary'
          tooltip={canWrite ? t('Edit') : t('View')}
          onClick={handleEdit}
          type={canWrite ? 'edit' : 'view'}
          disabled={disabled}
        />
        {canWrite ? (
          <IconButton
            color='primary'
            size='small'
            variant='text'
            type='delete'
            tooltip={t('Delete')}
            onClick={handleDelete}
            disabled={disabled}
          />
        ) : (
          <></>
        )}
      </ActionsTableCell>
    </TableRow>
  );
};

AccountStatementItem.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired
};

export default AccountStatementItem;
